import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { AnimatePresence, motion } from "framer-motion"
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Globe,
  Landmark,
  RefreshCw,
  Shield,
  TrendingUp,
  Wallet,
  Zap,
} from "lucide-react"
import { useState } from "react"

export const Route = createFileRoute("/features/accept-payments")({
  component: AcceptPaymentsPage,
})

const METHODS = [
  {
    id: "cards",
    icon: CreditCard,
    label: "Cards",
    bg: "bg-blue",
    light: "bg-blue-light",
    text: "text-blue",
    headline: "Every card network, globally",
    stat: "98.9%",
    statLabel: "approval rate",
    desc: "Visa, Mastercard, Amex, Discover, JCB, UnionPay in 180+ countries. Network tokenisation keeps credentials fresh automatically.",
    items: [
      "Visa & Mastercard",
      "American Express",
      "JCB & UnionPay",
      "Network tokens",
      "Prepaid & virtual",
      "Card fingerprinting",
    ],
    feed: [
      { flag: "🇺🇸", name: "Visa ···4242", amt: "$240", ok: true },
      { flag: "🇬🇧", name: "Mastercard ···1881", amt: "£890", ok: true },
      { flag: "🇩🇪", name: "Amex ···9021", amt: "€1,200", ok: true },
    ],
  },
  {
    id: "bank",
    icon: Landmark,
    label: "Bank transfers",
    bg: "bg-green-DEFAULT",
    light: "bg-green-light",
    text: "text-green",
    headline: "Direct debits & instant rails",
    stat: "0%",
    statLabel: "chargeback rate",
    desc: "SEPA, ACH, BACS, Open Banking — lower fees, zero chargebacks, instant confirmation on supported rails.",
    items: [
      "SEPA Credit Transfer",
      "SEPA Direct Debit",
      "ACH (US)",
      "BACS (UK)",
      "Open Banking",
      "FPS Instant",
    ],
    feed: [
      { flag: "🇪🇺", name: "SEPA Instant", amt: "€4,500", ok: true },
      { flag: "🇺🇸", name: "ACH Same-Day", amt: "$12,800", ok: true },
      { flag: "🇬🇧", name: "FPS", amt: "£7,200", ok: true },
    ],
  },
  {
    id: "wallets",
    icon: Wallet,
    label: "Digital wallets",
    bg: "bg-amber-DEFAULT",
    light: "bg-amber-light",
    text: "text-amber-DEFAULT",
    headline: "One tap. Zero friction.",
    stat: "+42%",
    statLabel: "vs card entry CVR",
    desc: "Apple Pay, Google Pay, PayPal — biometric one-tap checkout that converts 42% better than manual card entry.",
    items: ["Apple Pay", "Google Pay", "PayPal", "Click to Pay", "Samsung Pay", "Browser wallets"],
    feed: [
      { flag: "", name: "Apple Pay", amt: "$89", ok: true },
      { flag: "", name: "Google Pay", amt: "€45", ok: true },
      { flag: "", name: "PayPal", amt: "$320", ok: true },
    ],
  },
  {
    id: "local",
    icon: Globe,
    label: "Local methods",
    bg: "bg-rose-DEFAULT",
    light: "bg-rose-light",
    text: "text-rose-DEFAULT",
    headline: "40+ local methods, one API",
    stat: "40+",
    statLabel: "payment methods",
    desc: "iDEAL, Bancontact, PIX, OXXO, Sofort and 35 more. Right methods auto-presented by customer location.",
    items: ["iDEAL (NL)", "Bancontact (BE)", "PIX (BR)", "OXXO (MX)", "Sofort (DE)", "PayNow (SG)"],
    feed: [
      { flag: "🇳🇱", name: "iDEAL", amt: "€340", ok: true },
      { flag: "🇧🇷", name: "PIX", amt: "R$780", ok: true },
      { flag: "🇲🇽", name: "OXXO", amt: "MX$450", ok: false },
    ],
  },
]

const STREAM = [
  { flag: "🇺🇸", m: "Apple Pay", a: "$240", t: "just now" },
  { flag: "🇳🇱", m: "iDEAL", a: "€890", t: "1s ago" },
  { flag: "🇧🇷", m: "PIX", a: "R$1,200", t: "2s ago" },
  { flag: "🇩🇪", m: "SEPA", a: "€4,500", t: "4s ago" },
  { flag: "🇬🇧", m: "Visa ···4242", a: "£320", t: "6s ago" },
  { flag: "🇯🇵", m: "JCB ···7771", a: "¥48,000", t: "8s ago" },
]

function AcceptPaymentsPage() {
  const [tab, setTab] = useState(0)
  const m = METHODS[tab]
  useSEO({
    title: "Accept Payments — Orchestrate",
    description:
      "Cards, bank transfers, wallets and 40+ local payment methods. One API, 98.9% approval rate.",
  })
  return (
    <>
      {/* HERO */}
      <section className="relative flex min-h-[90vh] flex-col justify-center overflow-hidden pt-28">
        <div className="grid-bg absolute inset-0 opacity-[0.12]" aria-hidden />
        <div
          className="from-blue/10 pointer-events-none absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l to-transparent"
          aria-hidden
        />
        <div className="mx-auto w-full max-w-7xl px-6 py-16 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-8 text-6xl leading-[0.9] tracking-tight sm:text-7xl">
                  Every
                  <br />
                  <span className="from-blue-mid to-blue bg-gradient-to-r bg-clip-text text-transparent">
                    payment method.
                  </span>
                  <br />
                  One call.
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="mb-10 max-w-md text-xl leading-relaxed">
                  Cards, bank transfers, wallets, 40+ local methods. One API that works in every
                  country your customers are in.
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" size="lg" asChild>
                    <Link to="/contact">
                      Start accepting <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="white-outline" size="lg" asChild>
                    <a href="https://docs.orchestrate.global/">View docs</a>
                  </Button>
                </div>
              </AnimatedSection>
              <AnimatedSection delay={0.4}>
                <div className="mt-12 flex gap-8">
                  {[
                    { v: "180+", l: "Countries" },
                    { v: "98.9%", l: "Approval rate" },
                    { v: "40+", l: "Local methods" },
                  ].map((s) => (
                    <div key={s.l}>
                      <p className="font-display text-3xl">{s.v}</p>
                      <p className="mt-1 text-xs">{s.l}</p>
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
            <AnimatedSection direction="right" delay={0.25}>
              <div className="rounded-3xl border border-gray-200 bg-white p-7">
                <div className="mb-5 flex items-center justify-between">
                  <span className="text-sm font-semibold">Global transaction stream</span>
                  <span className="text-green flex items-center gap-1.5 text-xs font-bold">
                    <span className="bg-green-DEFAULT h-1.5 w-1.5 animate-pulse rounded-full" />
                    Live
                  </span>
                </div>
                {STREAM.map((tx, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.07 }}
                    className="flex items-center justify-between border-b border-white/10 py-3 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue text-xl">{tx.flag}</span>
                      <div>
                        <p className="text-sm font-medium">{tx.m}</p>
                        <p className="text-xs text-white/30">{tx.t}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{tx.a}</p>
                      <span className="text-green text-xs font-bold">✓ Approved</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* PAYMENT METHODS*/}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-10">
              <SectionLabel label="Payment Methods" className="mb-4" />
              <h2 className="font-display text-5xl tracking-tight">Explore every method</h2>
            </div>
          </AnimatedSection>
          <div className="mb-8 flex gap-2 overflow-x-auto pb-1">
            {METHODS.map((mt, i) => {
              const Icon = mt.icon
              return (
                <button
                  key={mt.id}
                  onClick={() => setTab(i)}
                  className={`flex flex-shrink-0 items-center gap-2 rounded-xl border px-5 py-3 text-sm font-semibold transition-all ${tab === i ? "bg-ink border-ink text-white shadow-lg" : "border-border text-muted hover:text-ink hover:border-blue-mid"}`}
                >
                  <Icon className="h-4 w-4" />
                  {mt.label}
                </button>
              )
            })}
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="grid gap-5 lg:grid-cols-5">
                <div className={`lg:col-span-3 ${m.light} border-border rounded-3xl border p-10`}>
                  <h3 className="font-display mb-4 text-4xl tracking-tight">{m.headline}</h3>
                  <p className="text-muted mb-8 max-w-lg text-lg leading-relaxed">{m.desc}</p>
                  <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {m.items.map((it) => (
                      <div
                        key={it}
                        className="flex items-center gap-2 rounded-xl border border-white bg-white/70 px-3 py-2 text-sm font-medium"
                      >
                        <CheckCircle2 className="text-green h-4 w-4 flex-shrink-0" />
                        {it}
                      </div>
                    ))}
                  </div>
                  <div className="border-border inline-flex items-baseline gap-3 rounded-2xl border bg-white p-5">
                    <span className={`font-display text-4xl ${m.text}`}>{m.stat}</span>
                    <span className="text-muted text-sm">{m.statLabel}</span>
                  </div>
                </div>
                <div className="bg-ink flex flex-col rounded-3xl p-8 lg:col-span-2">
                  <p className="mb-5 text-xs font-bold tracking-widest text-white/40 uppercase">
                    Live transactions
                  </p>
                  <div className="flex-1 space-y-3">
                    {m.feed.map((tx, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-4"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-blue-mid text-xl">{tx.flag}</span>
                          <span className="text-sm font-medium text-white">{tx.name}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white">{tx.amt}</p>
                          <span
                            className={`text-xs font-bold ${tx.ok ? "text-green" : "text-amber-DEFAULT"}`}
                          >
                            {tx.ok ? "✓ Approved" : "Pending"}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-6 border-t border-white/10 pt-5">
                    <p className="mb-3 text-xs text-white">Sample API call</p>
                    <pre className="text-blue-mid/80 font-mono text-xs leading-relaxed">{`await orchestrate.payments.create({
  amount: 9900,
  currency: 'USD',
  method: '${m.id}'
})`}</pre>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-14 text-center">
              <SectionLabel label="Capabilities" centered className="mb-4" />
              <h2 className="font-display text-5xl tracking-tight">
                Payments infrastructure
                <em className="text-blue not-italic"> done right</em>
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Zap,
                c: "bg-blue-light",
                ic: "text-blue",
                t: "One integration",
                d: "Every method through a single API. No per-provider code.",
              },
              {
                icon: Shield,
                c: "bg-green-light",
                ic: "text-green",
                t: "Fraud built-in",
                d: "ML scoring and 3DS 2.x protect every payment automatically.",
              },
              {
                icon: Globe,
                c: "bg-amber-light",
                ic: "text-amber-DEFAULT",
                t: "Auto-localisation",
                d: "Right methods, right currency by customer location.",
              },
              {
                icon: TrendingUp,
                c: "bg-rose-light",
                ic: "text-rose-DEFAULT",
                t: "Smart retries",
                d: "Soft declines retried via different routing to maximise revenue.",
              },
              {
                icon: RefreshCw,
                c: "bg-blue-light",
                ic: "text-blue",
                t: "Real-time webhooks",
                d: "Every event delivered to your endpoint in under 200ms.",
              },
              {
                icon: CheckCircle2,
                c: "bg-green-light",
                ic: "text-green",
                t: "PCI DSS Level 1",
                d: "Card data never touches your servers. We handle all scope.",
              },
              {
                icon: Landmark,
                c: "bg-amber-light",
                ic: "text-amber-DEFAULT",
                t: "Multi-acquirer routing",
                d: "Auto-failover across 15+ acquirers globally.",
              },
              {
                icon: CreditCard,
                c: "bg-rose-light",
                ic: "text-rose-DEFAULT",
                t: "Network tokens",
                d: "Cards auto-updated when customers get replacements.",
              },
            ].map((f, i) => {
              const Icon = f.icon
              return (
                <motion.div
                  key={f.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: (i % 4) * 0.07,
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  viewport={{ once: true }}
                >
                  <div className="border-border h-full rounded-2xl border bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <div
                      className={`h-9 w-9 ${f.c} mb-4 flex items-center justify-center rounded-xl`}
                    >
                      <Icon className={`h-4 w-4 ${f.ic}`} />
                    </div>
                    <h3 className="mb-1.5 text-sm font-semibold">{f.t}</h3>
                    <p className="text-muted text-xs leading-relaxed">{f.d}</p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  )
}
